import { isHotkey } from "is-hotkey";
import { BasePoint, Editor, Operation, Path, Point, PointRef, Range } from "slate";

import { RichEditor } from "../types";

interface ICannedResponsePluginCallbacks {
  onCannedResponseEnter?: (pointRef: PointRef, accept?: () => void) => void;
  onCannedResponseFinish?: () => void;
}

const CANNED_RESPONSE_SYMBOL = "#";

const calculateCannedFinish = (editor: Editor, start: Point, spacesAllowed = 0): BasePoint | undefined => {
  let boundaryPoint = Editor.after(editor, start, { unit: "word" });
  let spacesPresent = 0;

  if (!boundaryPoint) {
    return start;
  }

  const [[, startBlockPath]] = Editor.nodes(editor, {
    at: start,
    match: node => Editor.isBlock(editor, node),
    mode: "lowest",
  });
  const [[, boundaryBlockPath]] = Editor.nodes(editor, {
    at: start,
    match: node => Editor.isBlock(editor, node),
    mode: "lowest",
  });

  if (
    !Path.equals(startBlockPath, boundaryBlockPath) ||
    /^\s/.test(Editor.string(editor, Editor.range(editor, start, boundaryPoint)))
  ) {
    return start;
  }

  while (boundaryPoint) {
    const next = Editor.after(editor, boundaryPoint, { unit: "character" });
    const nextCharacter = Editor.string(editor, Editor.range(editor, boundaryPoint, next));
    const [[, nextBlockPath]] = Editor.nodes(editor, {
      at: next,
      match: node => Editor.isBlock(editor, node),
      mode: "lowest",
    });

    if (nextCharacter.length === 0 || !Path.equals(startBlockPath, nextBlockPath)) {
      break;
    }

    if (/[^\s]/.test(nextCharacter)) {
      boundaryPoint = next;
    } else if (spacesPresent < spacesAllowed) {
      spacesPresent += 1;
      boundaryPoint = next;
    } else {
      break;
    }

    if (!boundaryPoint) {
      break;
    }

    const nextWordProposal = Editor.after(editor, boundaryPoint, { unit: "word" });
    if (nextWordProposal) {
      const [[, nextWordBlockPath]] = Editor.nodes(editor, {
        at: nextWordProposal,
        match: node => Editor.isBlock(editor, node),
        mode: "lowest",
      });

      if (
        !/^\s/.test(Editor.string(editor, Editor.range(editor, boundaryPoint, nextWordProposal))) &&
        spacesPresent < spacesAllowed &&
        Path.equals(nextBlockPath, nextWordBlockPath)
      ) {
        boundaryPoint = nextWordProposal;
      }
    }
  }

  return boundaryPoint;
};

const withCannedResponses =
  (callbacks: ICannedResponsePluginCallbacks) =>
  (editor: Editor): RichEditor => {
    const { insertText, apply } = editor;

    const privateAPI: { pointRef: PointRef | null; allowedSpaces: number } = {
      pointRef: null,
      allowedSpaces: 0,
    };

    editor.cannedResponsesAPI = {
      onCannedResponseEnter: (): void => {},
      onCannedResponseFinish: (): void => {},
      ...callbacks,
      stop(): void {
        if (privateAPI.pointRef) {
          privateAPI.pointRef.unref();
          privateAPI.pointRef = null;
          this.onCannedResponseFinish();
        }
      },
      active(): boolean {
        return Boolean(privateAPI.pointRef && privateAPI.pointRef.current !== null);
      },
      range(): Range | null {
        if (!this.active()) {
          return null;
        }

        const { pointRef } = privateAPI;
        if (!pointRef?.current) return null;

        const hashStartPoint = Editor.after(editor, pointRef.current, { unit: "character" });
        if (!hashStartPoint) return null;

        const cannedFinishPoint = calculateCannedFinish(editor, hashStartPoint, privateAPI.allowedSpaces);
        if (!cannedFinishPoint) return null;

        if (editor.selection && Point.isAfter(cannedFinishPoint, editor.selection.anchor)) {
          return Editor.range(editor, pointRef.current, editor.selection.anchor);
        }

        return Editor.range(editor, pointRef.current, cannedFinishPoint);
      },
      setAllowedSpaces(spaces: number): void {
        privateAPI.allowedSpaces = spaces;
      },
    };

    editor.insertText = (text: string): void => {
      insertText(text);

      if (text === CANNED_RESPONSE_SYMBOL && editor.selection) {
        const point = Editor.before(editor, Range.start(editor.selection), { unit: "character" });
        if (!point) return;

        const pointRef = Editor.pointRef(editor, point);

        const accept = (): void => {
          privateAPI.pointRef = pointRef;
        };

        editor.cannedResponsesAPI.onCannedResponseEnter(pointRef, accept);
      }
    };

    editor.apply = (op: Operation): void => {
      apply(op);

      if (editor.cannedResponsesAPI.active()) {
        const { selection } = editor;
        const { pointRef } = privateAPI;

        if (selection === null) {
          editor.cannedResponsesAPI.stop();
          editor.cannedResponsesAPI.onCannedResponseFinish();
        } else {
          if (!pointRef?.current) return;

          const hashStartPoint = Editor.after(editor, pointRef.current, { unit: "character" });
          if (!hashStartPoint) {
            editor.cannedResponsesAPI.stop();
            return;
          }

          const cannedFinishPoint = calculateCannedFinish(editor, hashStartPoint, privateAPI.allowedSpaces);
          if (!cannedFinishPoint) {
            editor.cannedResponsesAPI.stop();
            return;
          }

          const hashInPlace =
            Editor.string(editor, Editor.range(editor, pointRef.current, hashStartPoint)) === CANNED_RESPONSE_SYMBOL;
          const cursorBeforeCannedHash = Point.isBefore(selection.anchor, pointRef.current);

          if (!hashInPlace || cursorBeforeCannedHash) {
            editor.cannedResponsesAPI.stop();
          }
        }
      }
    };

    return editor;
  };

export default function createCannedResponsesPlugin(
  callbacks: ICannedResponsePluginCallbacks
): import("./index").PluginSpec {
  const configuredPlugin = withCannedResponses(callbacks);

  return [
    configuredPlugin,
    {
      onKeyDown(event, editor, next): void {
        const cannedResponsesActive = editor.cannedResponsesAPI.active();
        if (
          cannedResponsesActive &&
          ["right", "left", "up", "down", "enter"].some(hotkey => isHotkey(hotkey, event.nativeEvent))
        ) {
          event.preventDefault();
        }

        next(event);
      },
    },
  ];
}
